import React, { useState, useEffect } from 'react';
import { Users, Check, X, Clock, Mail, User, Trophy, School } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const TeamJoinRequests = ({ user }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingRequest, setProcessingRequest] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    fetchJoinRequests();
    
    // Refresh requests every 30 seconds
    const interval = setInterval(fetchJoinRequests, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchJoinRequests = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/teams/join-requests`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      } else {
        console.error('Failed to fetch join requests');
      }
    } catch (error) {
      console.error('Error fetching join requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = async (requestId, action) => {
    setProcessingRequest(requestId);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/teams/join-requests/${requestId}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          request_id: requestId,
          action: action,
          response_message: responseMessage
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        toast({
          title: action === 'approve' ? 'Request Approved' : 'Request Denied',
          description: result.message
        });
        
        // Remove the processed request from the list
        setRequests(prev => prev.filter(req => req.id !== requestId));
        setResponseMessage('');
      } else {
        const error = await response.json();
        throw new Error(error.detail || `Failed to ${action} request`);
      }
    } catch (error) {
      console.error('Error processing request:', error);
      toast({
        title: 'Error',
        description: error.message || `Failed to ${action} request`,
        variant: 'destructive'
      });
    } finally {
      setProcessingRequest(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Only show for coaches, team admins, and system admins
  if (!user || !['coach', 'team-admin', 'system-admin'].includes(user.role)) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading team join requests...</p>
        </div>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 text-center">
        <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Pending Requests</h3>
        <p className="text-gray-600">There are no team join requests waiting for approval.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <Users className="w-6 h-6 text-emerald-600" />
        <h2 className="text-xl font-bold text-gray-900">Team Join Requests</h2>
        <span className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full text-sm font-medium">
          {requests.length} pending
        </span>
      </div>

      <div className="space-y-4">
        {requests.map((request) => (
          <div key={request.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="bg-emerald-100 p-2 rounded-full">
                    <User className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{request.athlete_name}</h3>
                    <p className="text-sm text-gray-600">{request.athlete_email}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span className="font-medium text-gray-700">Team:</span>
                    <span>{request.team_name}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Trophy className="w-4 h-4 text-gray-400" />
                    <span className="font-medium text-gray-700">Sport:</span>
                    <span>{request.sport}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <School className="w-4 h-4 text-gray-400" />
                    <span className="font-medium text-gray-700">School:</span>
                    <span>{request.school_club_name}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span className="font-medium text-gray-700">Requested:</span>
                    <span>{formatDate(request.created_at)}</span>
                  </div>
                </div>

                {request.level && (
                  <div className="mb-4">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {request.level}
                    </span>
                    {request.gender && (
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {request.gender}
                      </span>
                    )}
                  </div>
                )}

                {/* Optional response message input */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Optional message to athlete:
                  </label>
                  <textarea
                    value={responseMessage}
                    onChange={(e) => setResponseMessage(e.target.value)}
                    rows={2}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                    placeholder="Add a welcome message or additional instructions..."
                    disabled={processingRequest === request.id}
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-3 pt-4 border-t border-gray-100">
              <button
                onClick={() => handleResponse(request.id, 'approve')}
                disabled={processingRequest === request.id}
                className="flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processingRequest === request.id ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Check className="w-4 h-4" />
                )}
                <span>Approve & Add to Team</span>
              </button>

              <button
                onClick={() => handleResponse(request.id, 'deny')}
                disabled={processingRequest === request.id}
                className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="w-4 h-4" />
                <span>Deny Request</span>
              </button>

              <button
                onClick={() => window.open(`mailto:${request.athlete_email}`, '_blank')}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors"
              >
                <Mail className="w-4 h-4" />
                <span>Contact Athlete</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TeamJoinRequests;